import { useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { resizeImage, reformatDate } from "../../shared/utils";
import { useCurrentViewportView } from "../../hooks/useCurrentViewportView";
import Skeleton from "../Common/Skeleton";
import ReadMore from "../Common/ReadMore";
import ReviewTab from "./ReviewTab";

const FilmTabInfo = ({ detail, credits, reviews }) => {
  const [currentTab, setCurrentTab] = useState("overall");
  const { isMobile } = useCurrentViewportView();

  return (
    <>
      <div className="flex sm:gap-1 md:gap-4 text-gray-400 text-lg justify-center">
        <button
          className={`px-4 py-2 mr-2 mb-5 rounded-full uppercase bg-dark-darken text-sm font-medium transition duration-300 shadow-sm shadow-gray-darken
                    hover:shadow-md hover:shadow-gray-darken hover:text-white ${
                      currentTab === "overall" &&
                      "bg-white text-dark-darken hover:text-dark-darken"
                    }`}
          onClick={() => setCurrentTab("overall")}
        >
          Overall
        </button>
        <button
          className={`px-4 py-2 mr-2 mb-5 rounded-full uppercase bg-dark-darken text-sm font-medium transition duration-300 shadow-sm shadow-gray-darken
                    hover:shadow-md hover:shadow-gray-darken hover:text-white  ${
                      currentTab === "cast" &&
                      "bg-white text-dark-darken hover:text-dark-darken"
                    }`}
          onClick={() => setCurrentTab("cast")}
        >
          Cast
        </button>
        <button
          className={`px-4 py-2 mr-2 mb-5 rounded-full uppercase bg-dark-darken text-sm font-medium transition duration-300 shadow-sm shadow-gray-darken
                    hover:shadow-md hover:shadow-gray-darken hover:text-white  ${
                      currentTab === "reviews" &&
                      "bg-white text-dark-darken hover:text-dark-darken"
                    }`}
          onClick={() => setCurrentTab("reviews")}
        >
          Reviews
        </button>
        {detail.media_type === "tv" && (
          <button
            className={`px-4 py-2 mr-2 mb-5 rounded-full uppercase bg-dark-darken text-sm font-medium transition duration-300 shadow-sm shadow-gray-darken
                      hover:shadow-md hover:shadow-gray-darken hover:text-white  ${
                        currentTab === "seasons" &&
                        "bg-white text-dark-darken hover:text-dark-darken"
                      }`}
            onClick={() => setCurrentTab("seasons")}
          >
            Seasons
          </button>
        )}
      </div>
      <div className="mt-10 text-lg">
        {currentTab === "overall" && (
          <>
            {detail && (
              <p className="text-xl italic mb-8 text-white text-center">
                {detail.tagline}
              </p>
            )}
            {!detail && <Skeleton className="h-6 w-[350px] mx-auto mb-8" />}
            {detail && (
              <>
                <p className="text-white font-medium  mb-3">STORY</p>
                <ReadMore limitTextLength={250}>{detail.overview}</ReadMore>
              </>
            )}
            {!detail && <Skeleton className="h-20" />}
            {detail && (
              <p className="text-white font-medium mt-8 mb-3">DETAILS</p>
            )}
            {detail && (
              <div className="w-100 h-100 flex flex-col items-center justify-center md:flex-row md:flex-wrap md:justify-start gap-5 my-5">
                <div className="flex flex-col justify-center text-center w-fit h-fit rounded-md">
                  <p className="px-4 py-2 bg-white text-dark-darken w-100 rounded-t-md mx-4">
                    Status
                  </p>
                  <p className="px-4 py-2 text-gray-200 shadow-md shadow-gray-darken bg-gradient-to-t from-transparent to-gray-darken/60  w-100 rounded-b-md">
                    {detail.status}
                  </p>
                </div>
                <div className="flex flex-col justify-center text-center w-fit h-fit rounded-md">
                  <p className="px-4 py-2 bg-white text-dark-darken w-100 rounded-t-md mx-4">
                    {detail.media_type === "movie"
                      ? "Release date"
                      : "Last Air Date"}
                  </p>
                  <p className="px-4 py-2 text-gray-200 shadow-md shadow-gray-darken bg-gradient-to-t from-transparent to-gray-darken/60 w-100 rounded-b-md">
                    {detail.media_type === "movie"
                      ? reformatDate(detail.release_date)
                      : reformatDate(detail.last_air_date)}
                  </p>
                </div>
                <div className="flex flex-col justify-center text-center w-fit h-fit rounded-md">
                  <p className="px-4 py-2 bg-white text-dark-darken w-100 rounded-t-md mx-4">
                    Spoken Languages
                  </p>
                  <p className="px-4 py-2 text-gray-200 shadow-md shadow-gray-darken bg-gradient-to-t from-transparent to-gray-darken/60 w-100 rounded-b-md">
                    {detail.spoken_languages.map(
                      (language, index) =>
                        `${index ? ", " : ""} ${language.english_name}`
                    )}
                  </p>
                </div>
              </div>
            )}
            {!detail && (
              <div className="w-100 h-100 flex flex-col items-center justify-center md:flex-row md:flex-wrap md:justify-start gap-5 my-5">
                {[...new Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="flex flex-col justify-center text-center w-fit h-fit rounded-md"
                  >
                    <Skeleton className="h-10 w-24 rounded-t-md mx-4" />
                    <Skeleton className="h-10 w-32 rounded-b-md" />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {currentTab === "cast" && (
          <ul className="grid md:grid-cols-2 gap-x-10 gap-y-8">
            {credits.length > 0 &&
              credits.map((cast) => (
                <li
                  key={cast.id}
                  className="flex gap-3 items-center p-3 rounded-l-full bg-gradient-to-l from-transparent to-gray-darken/60 
                              hover:brightness-[115%] transition duration-300 group"
                >
                  <div className="shrink-0 max-w-[75px] w-full h-[75px]">
                    <LazyLoadImage
                      src={resizeImage(cast.profile_path, "w185")}
                      alt=""
                      effect="opacity"
                      className="object-cover rounded-full h-[75px] w-[75px]"
                    />
                  </div>
                  <div className="flex-grow">
                    <p className="text-gray-200 group-hover:text-white">
                      {cast.name}
                    </p>
                    <p className="text-base">
                      <span className="italic">as</span> {cast.character}
                    </p>
                  </div>
                </li>
              ))}
            {!credits.length > 0 &&
              [...new Array(8)].map((_, index) => (
                <li key={index} className="flex gap-3 items-center">
                  <Skeleton className="h-[85px] w-[325px] rounded-l-full" />
                </li>
              ))}
          </ul>
        )}
        {currentTab === "reviews" && reviews && <ReviewTab reviews={reviews} />}
        {currentTab === "seasons" && (
          <>
            {detail && (
              <div className="flex justify-between mb-8 mx-3">
                <p>Total seasons : {detail.number_of_seasons}</p>
                <p>Total episodes : {detail.number_of_episodes}</p>
              </div>
            )}
            {!detail && (
              <div className="flex justify-between mb-8 mx-3">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-6 w-40" />
              </div>
            )}

            <ul className="w-full h-full overflow-auto flex flex-col gap-8">
              {detail.seasons.map((season, index) => {
                if (season.episode_count && season.poster_path)
                  return (
                    <li key={season.id}>
                      <div
                        className={`p-3 bg-gradient-to-l from-transparent to-gray-darken/60 m-3 rounded-md flex ${
                          index % 2 &&
                          "flex-row-reverse bg-gradient-to-r from-transparent to-gray-darken/60"
                        } gap-3 items-start group hover:scale-[103%] hover:brightness-125 transition duration-300`}
                      >
                        <div className="shrink-0 max-w-[150px] w-full">
                          {isMobile && (
                            <p
                              className={`mb-12 text-gray-200 group-hover:text-white group-hover:font-medium transition duration-300 ${
                                index % 2 && "text-right"
                              }`}
                            >
                              {season.name}
                            </p>
                          )}
                          <LazyLoadImage
                            src={resizeImage(season.poster_path, "w200")}
                            alt=""
                            effect="opacity"
                            className="object-cover w-[150px] h-full rounded-md"
                          />
                        </div>
                        <div className="md:p-2 flex-grow">
                          {isMobile ? (
                            <div className="flex flex-col mb-5">
                              <p>{season.episode_count} episodes</p>
                              <p className="text-base">
                                {reformatDate(season.air_date)}
                              </p>
                            </div>
                          ) : (
                            <div className="flex justify-between">
                              <p className="text-gray-200 group-hover:text-white group-hover:font-medium transition duration-300">
                                {season.name}
                              </p>
                              <p>{season.episode_count} episodes</p>
                            </div>
                          )}
                          {season.overview && (
                            <ReadMore
                              limitTextLength={130}
                              className="mb-2 inline-block"
                            >
                              {season.overview}
                            </ReadMore>
                          )}
                          <div className="flex justify-end">
                            {!isMobile && (
                              <p className="text-base">
                                {reformatDate(season.air_date)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                return null;
              })}
              {!detail && (
                <Skeleton className="h-60 w-160 rounded-md md:ml-3" />
              )}
            </ul>
          </>
        )}
      </div>
    </>
  );
};
export default FilmTabInfo;
